'use client';

// Lib dependencies
import { useCallback, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// UI Lib dependencies
import {
  Button,
  Container,
  Divider,
  FileButton,
  LoadingOverlay,
  Stack,
} from '@mantine/core';

// UI local dependencies
import UploadArea from '@ui-kit/molecules/UploadArea';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Services
import useUploader from '@services/upload/useUploader';

// API & types
import { Resource, useGetOneResourceQuery } from '@app-types/blackroom-api';
import { ResourceMimeType } from '@app-types/blackroom-app/resourceMimeType';

// Assets
import {
  IconFileUpload as IconUploadFiles,
  IconFolderUp as IconUploadFolder,
} from '@tabler/icons-react';
import { palette } from '@ui-kit/colors';
import { UploadFilesData } from './types';
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

/**
 * The inner content of the add resource modal, managed
 * by the modale provider
 */
function DialogResourceUpload({
  onUploadFiles,
  w = '30vw',
}: {
  onUploadFiles?: (data: UploadFilesData) => any;
  w?: string | number;
}) {
  /* ********************************** HOOKS ********************************* */
  const workspace = useWorkspace();

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);

  // Services
  const { uploadSelectedResources, validateAndUploadFiles } = useUploader();

  // Routing
  const { folderId: parentId } = useParams();

  // API
  const { data: parentData } = useGetOneResourceQuery({
    variables: { workspaceId: workspace.id, resourceId: parentId as string },
    skip: !parentId,
  });

  // Localization
  const { t } = useTranslation();

  /* ***************************** LOCAL VARIABLES **************************** */

  const parent = parentData?.resource as Resource;

  /* ********************************** STATE ********************************* */

  const [isValidating, setIsValidating] = useState(false);

  /* ******************************** CALLBACKS ******************************* */

  const onUploadFilesDefault = useCallback(
    (files: UploadFilesData, options?: { onReady?: () => void }) => {
      dialogStore?.closeDialog?.('dialog-resource-upload');
      uploadSelectedResources({
        parent,
        files,
        onReady: options?.onReady,
      });
    },
    [parent],
  );

  const uploadFiles = onUploadFiles ?? onUploadFilesDefault;

  const onSelectFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;

      setIsValidating(true);

      await validateAndUploadFiles({
        files,
        parent,
        ...(onUploadFiles && {
          createResourcesFn: async ({ resources }) => {
            onUploadFiles(resources as unknown as UploadFilesData);
          },
        }),
        onValidationComplete: () => {
          setIsValidating(false);
          dialogStore?.closeDialog?.('dialog-resource-upload');
        },
      });
    },
    [validateAndUploadFiles, parent, dialogStore, onUploadFiles],
  );

  /* ******************************** RENDERING ******************************* */

  return (
    <Container w={w} px={0} fluid pos="relative">
      <LoadingOverlay visible={isValidating} overlayProps={{ blur: 2 }} />
      <Stack>
        <FileButton accept="*" onChange={onSelectFiles} multiple>
          {(props) => (
            <Button
              size="xl"
              fz="md"
              leftSection={<IconUploadFiles />}
              {...props}
            >
              {t('Resource:DialogResourceUpload.ctaUploadFiles.label')}
            </Button>
          )}
        </FileButton>
        <FileButton
          accept={Object.values(ResourceMimeType).join(',')}
          onChange={onSelectFiles}
          inputProps={
            { directory: '', webkitdirectory: '', mozdirectory: '' } as any
          }
          multiple
        >
          {(props) => (
            <Button
              variant="outline"
              size="xl"
              fz="md"
              color="blackroomGray"
              c={palette.blackroomDarkGray4}
              leftSection={<IconUploadFolder />}
              {...props}
            >
              {t('Resource:DialogResourceUpload.ctaUploadFolder.label')}
            </Button>
          )}
        </FileButton>
        <Divider
          label={t('Common:operators.or')}
          orientation="horizontal"
          color="blackroomGray"
          tt="uppercase"
        />
        <UploadArea h="15vh" onAddedFiles={(entries) => uploadFiles(entries)} />
      </Stack>
    </Container>
  );
}

export default DialogResourceUpload;
